// ==========================================================================
// lib/appearance.ts — light or dark, per device (FF-058, 10 Oct 2026)
//   Auto follows the phone or computer's own setting (as before). My profile
//   can pin Light or Dark for this device. Kept in the browser, not the
//   database: the phone and the desktop can differ.
//   Sets <html data-mode="light|dark">; theme.css reads it.
// ==========================================================================
import { useEffect, useState } from "react";

export type Mode = "auto" | "light" | "dark";
export const MODES: { key: Mode; label: string }[] = [
  { key: "auto", label: "Auto" },
  { key: "light", label: "Light" },
  { key: "dark", label: "Dark" },
];

const mql = typeof window !== "undefined" ? window.matchMedia("(prefers-color-scheme: dark)") : null;

export function getMode(): Mode {
  try { const m = localStorage.getItem("mode"); if (m === "light" || m === "dark" || m === "auto") return m; } catch { /* private window */ }
  return "auto";
}

/** Puts the resolved light/dark on <html>. */
export function applyMode(m: Mode = getMode()) {
  const dark = m === "dark" || (m === "auto" && !!mql?.matches);
  document.documentElement.dataset.mode = dark ? "dark" : "light";
}

let listening = false;
/** Call once at start-up: applies the saved choice and follows the phone while on Auto. */
export function startAppearance() {
  applyMode();
  if (!listening && mql) { mql.addEventListener("change", () => applyMode()); listening = true; }
}

export function isDark() {
  return document.documentElement.dataset.mode === "dark";
}

export function useMode(): [Mode, (m: Mode) => void] {
  const [mode, setMode] = useState<Mode>(getMode);
  useEffect(() => { applyMode(mode); }, [mode]);
  return [mode, (m: Mode) => { try { localStorage.setItem("mode", m); } catch { /* fine */ } setMode(m); }];
}
